/*
 * scroll-header.js — Fallback StickyHeader implementation for Dawn.
 *
 * Shopify compiles {% javascript %} blocks from section files into a CDN
 * bundle. If that bundle fails to register the sticky-header custom element,
 * this script registers it instead. When the compiled bundle IS working this
 * script exits immediately without doing anything.
 *
 * Loaded via <script defer> from sections/header.liquid.
 */
(function () {
  'use strict';

  /* Defer until DOMContentLoaded so the compiled bundle (also deferred) has
     had its chance to run first and register the custom element. */
  function maybeRegister() {
    if (customElements.get('sticky-header')) return; /* bundle worked — done */

    class StickyHeader extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.header = document.querySelector('.section-header');
        if (!this.header) return;

        this.headerIsAlwaysSticky =
          this.getAttribute('data-sticky-type') === 'always' ||
          this.getAttribute('data-sticky-type') === 'reduce-logo-size';
        this.headerBounds = {};

        this.setHeaderHeight();

        window
          .matchMedia('(max-width: 990px)')
          .addEventListener('change', this.setHeaderHeight.bind(this));

        if (this.headerIsAlwaysSticky) {
          this.header.classList.add('shopify-section-header-sticky');
        }

        this.currentScrollTop = 0;
        this.preventReveal = false;
        this.predictiveSearch = this.querySelector('predictive-search');

        this.onScrollHandler = this.onScroll.bind(this);
        this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

        this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        window.addEventListener('scroll', this.onScrollHandler, { passive: true });

        this.createObserver();
      }

      setHeaderHeight() {
        if (!this.header) return;
        document.documentElement.style.setProperty(
          '--header-height',
          this.header.offsetHeight + 'px'
        );
      }

      disconnectedCallback() {
        this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        window.removeEventListener('scroll', this.onScrollHandler);
      }

      createObserver() {
        const observer = new IntersectionObserver((entries, obs) => {
          this.headerBounds = entries[0].intersectionRect;
          obs.disconnect();
        });
        observer.observe(this.header);
      }

      onScroll() {
        const scrollTop =
          window.pageYOffset !== undefined
            ? window.pageYOffset
            : document.documentElement.scrollTop;

        if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

        /* Ignore micro-movements to prevent flicker from iOS chrome-resize
           events and trackpad momentum. Threshold is cumulative — the next
           event that exceeds 5 px from the last acted-on position fires. */
        if (Math.abs(scrollTop - this.currentScrollTop) < 5) return;

        if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
          this.header.classList.add('scrolled-past-header');
          if (this.preventHide) return;
          requestAnimationFrame(this.hide.bind(this));
        } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
          this.header.classList.add('scrolled-past-header');
          if (!this.preventReveal) {
            requestAnimationFrame(this.reveal.bind(this));
          } else {
            window.clearTimeout(this.isScrolling);
            this.isScrolling = setTimeout(() => {
              this.preventReveal = false;
            }, 66);
            requestAnimationFrame(this.hide.bind(this));
          }
        } else if (scrollTop <= this.headerBounds.top) {
          this.header.classList.remove('scrolled-past-header');
          requestAnimationFrame(this.reset.bind(this));
        }

        this.currentScrollTop = scrollTop;
      }

      hide() {
        if (this.headerIsAlwaysSticky) return;
        /* Two-step: ensure sticky (top:0) is the "from" value before adding
           hidden. CSS cannot transition from top:auto, so without the forced
           reflow the very first hide snaps instead of sliding. */
        if (!this.header.classList.contains('shopify-section-header-sticky')) {
          this.header.classList.add('shopify-section-header-sticky');
          void this.header.offsetHeight;
        }
        this.header.classList.add('shopify-section-header-hidden');
        this.closeMenuDisclosure();
        this.closeSearchModal();
      }

      reveal() {
        if (this.headerIsAlwaysSticky) return;
        this.header.classList.add('shopify-section-header-sticky', 'animate');
        this.header.classList.remove('shopify-section-header-hidden');
      }

      reset() {
        if (this.headerIsAlwaysSticky) return;
        this.header.classList.remove(
          'shopify-section-header-hidden',
          'shopify-section-header-sticky',
          'animate'
        );
      }

      closeMenuDisclosure() {
        this.disclosures =
          this.disclosures || this.header.querySelectorAll('header-menu');
        this.disclosures.forEach((d) => d.close && d.close());
      }

      closeSearchModal() {
        this.searchModal =
          this.searchModal || this.header.querySelector('details-modal');
        if (this.searchModal && this.searchModal.close) {
          this.searchModal.close(false);
        }
      }
    }

    try {
      customElements.define('sticky-header', StickyHeader);
    } catch (_) {
      /* Bundle registered it between our check and here — that is fine. */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', maybeRegister);
  } else {
    maybeRegister();
  }
})();
